import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Users, Loader2, Search, MoreVertical, ShieldCheck, UserCog, Ban, UserCheck, Trash2, AlertTriangle } from "lucide-react";
import { PERSONA_OPTIONS } from "@/lib/personas";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";

const roleColors = { USER: "bg-blue-100 text-blue-800", PROVIDER: "bg-purple-100 text-purple-800", PLATFORM_ADMIN: "bg-court-orange/10 text-court-orange" };

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [user360, setUser360] = useState(null);
  const [user360Open, setUser360Open] = useState(false);

  const fetchUsers = async () => {
    try {
      const params = {};
      if (roleFilter !== "all") params.role = roleFilter;
      if (query) params.q = query;
      const [usersRes, requestsRes] = await Promise.all([
        api.get("/admin/users", { params }),
        api.get("/admin/persona-requests", { params: { status: "pending" } }),
      ]);
      setUsers(usersRes.data);
      setRequests(requestsRes.data.requests || []);
    } catch { setUsers([]); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); fetchUsers(); }, [roleFilter]); // eslint-disable-line

  const handleSearch = () => { setLoading(true); fetchUsers(); };

  const grantPersona = async (userId, personaType) => {
    try {
      await api.post(`/admin/personas/grant`, { user_id: userId, persona_type: personaType });
      toast.success(`${personaType} persona granted`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const removePersona = async (userId, personaType) => {
    try {
      await api.post(`/admin/personas/remove`, { user_id: userId, persona_type: personaType });
      toast.success(`${personaType} persona removed`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const reviewRequest = async (requestId, action) => {
    try {
      await api.post(`/admin/persona-requests/${requestId}/review`, { action });
      toast.success(`Request ${action}d`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const changeStatus = async (userId, newStatus) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`User ${newStatus.toLowerCase()}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userToDelete.user_id}`);
      toast.success(`User "${userToDelete.name}" has been permanently deleted.`);
      closeDeleteDialog();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const openUser360 = async (userId) => {
    navigate(`/admin/users/${userId}/360`);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  const filteredUsers = users.filter((user) => statusFilter === "all" || (user.status || "ACTIVE") === statusFilter);
  const activeFilters = [
    query ? { key: "search", label: `Search: ${query}`, onRemove: () => setQuery("") } : null,
    roleFilter !== "all" ? { key: "persona", label: `Persona: ${roleFilter}`, onRemove: () => setRoleFilter("all") } : null,
    statusFilter !== "all" ? { key: "status", label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("all") } : null,
  ].filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-users-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <Users className="w-8 h-8 text-court-orange" />Users
      </h1>
      <p className="text-muted-foreground mb-6">Manage user accounts, personas, and persona requests</p>

      {requests.length > 0 && (
        <div className="mb-6 bg-white border-2 border-amber-100 p-4" data-testid="persona-requests-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-sm uppercase tracking-[0.15em] text-amber-700">Pending Persona Requests</h2>
            <Badge className="bg-amber-100 text-amber-800">{requests.length} pending</Badge>
          </div>
          <div className="space-y-2">
            {requests.map((req) => (
              <div key={req.request_id} className="border border-slate-100 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`persona-request-${req.request_id}`}>
                <div>
                  <p className="font-medium text-sm">{req.name} — {req.persona_label}</p>
                  <p className="text-xs text-muted-foreground">{req.email}</p>
                  {req.message && <p className="text-xs text-muted-foreground mt-1">“{req.message}”</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => reviewRequest(req.request_id, "approve")} data-testid={`approve-request-${req.request_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reviewRequest(req.request_id, "reject")} data-testid={`reject-request-${req.request_id}`}>Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AdminFilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search by name or email..."
        searchTestId="user-search-input"
        filters={[
          { key: "persona", label: "Persona", value: roleFilter, onChange: setRoleFilter, options: [{ value: "all", label: "All Personas" }, { value: "PLAYER", label: "Players" }, { value: "VENUE_OWNER", label: "Venue Owners" }, { value: "COACH_SP", label: "Coach / Trainer / SP" }, { value: "PLATFORM_ADMIN", label: "Admins" }], testId: "user-role-filter" },
          { key: "status", label: "Status", value: statusFilter, onChange: setStatusFilter, options: [{ value: "all", label: "All Statuses" }, { value: "ACTIVE", label: "Active" }, { value: "SUSPENDED", label: "Suspended" }], testId: "user-status-filter" },
        ]}
        activeFilters={activeFilters}
        onClearAll={() => { setQuery(""); setRoleFilter("all"); setStatusFilter("all"); }}
        extraActions={<Button data-testid="user-search-btn" onClick={handleSearch} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-md font-heading uppercase"><Search className="w-4 h-4 mr-2" />Apply</Button>}
      />

      <p className="text-sm text-muted-foreground mb-3">{filteredUsers.length} users found</p>

      <div className="bg-white border-2 border-slate-100 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-heading uppercase tracking-wider text-xs">User</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Personas</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Status</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs">Joined</TableHead>
              <TableHead className="font-heading uppercase tracking-wider text-xs text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map(u => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" /> :
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-xs font-bold text-slate-500">{u.name?.[0]?.toUpperCase()}</span>
                      </div>}
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-[10px] text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(u.persona_labels || []).map((label) => (
                      <Badge key={label} className="text-[10px] font-heading uppercase bg-slate-100 text-slate-800">
                        {label}
                      </Badge>
                    ))}
                    {u.pending_persona_requests > 0 && <Badge className="bg-amber-100 text-amber-800 text-[10px]">{u.pending_persona_requests} pending</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] font-heading uppercase ${u.status === "SUSPENDED" ? 'text-red-600 border-red-200' : 'text-green-600 border-green-200'}`}>
                    {u.status || "ACTIVE"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid={`user-actions-${u.user_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => openUser360(u.user_id)} data-testid={`view-user-360-${u.user_id}`}>
                        <UserCheck className="w-4 h-4 mr-2" />View User 360
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {PERSONA_OPTIONS.filter((persona) => !(u.personas || []).includes(persona.key)).map((persona) => (
                        <DropdownMenuItem key={`grant-${persona.key}`} onClick={() => grantPersona(u.user_id, persona.key)}>
                          <UserCog className="w-4 h-4 mr-2" />Grant {persona.label}
                        </DropdownMenuItem>
                      ))}
                      {(u.personas || []).filter((persona) => ["PLAYER", "VENUE_OWNER", "COACH_SP"].includes(persona)).map((persona) => (
                        <DropdownMenuItem key={`remove-${persona}`} onClick={() => removePersona(u.user_id, persona)}>
                          <Ban className="w-4 h-4 mr-2" />Remove {persona === "PLAYER" ? "Player" : persona === "VENUE_OWNER" ? "Venue Owner" : "Coach / Trainer / SP"}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem onClick={() => changeStatus(u.user_id, "ACTIVE")} disabled={u.role === "PLATFORM_ADMIN"}>
                        <ShieldCheck className="w-4 h-4 mr-2" />Keep base account active
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {(!u.status || u.status === "ACTIVE") ? (
                        <DropdownMenuItem data-testid={`suspend-user-${u.user_id}`} onClick={() => changeStatus(u.user_id, "SUSPENDED")} className="text-red-600">
                          <Ban className="w-4 h-4 mr-2" />Suspend User
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem data-testid={`activate-user-${u.user_id}`} onClick={() => changeStatus(u.user_id, "ACTIVE")} className="text-green-600">
                          <UserCheck className="w-4 h-4 mr-2" />Activate User
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        data-testid={`delete-user-${u.user_id}`} 
                        onClick={() => openDeleteDialog(u)} 
                        className="text-red-600"
                        disabled={u.role === "PLATFORM_ADMIN"}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />Delete User
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User Permanently?
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the user account and remove all associated data.
            </DialogDescription>
          </DialogHeader>
          
          {userToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  {userToDelete.picture ? (
                    <img src={userToDelete.picture} alt="" className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-lg font-bold text-slate-500">{userToDelete.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold">{userToDelete.name}</p>
                    <p className="text-sm text-muted-foreground">{userToDelete.email}</p>
                  </div>
                </div>
                <div className="text-sm text-red-700">
                  <p className="font-medium mb-1">The following will be deleted:</p>
                  <ul className="list-disc list-inside text-xs space-y-0.5">
                    <li>User account and profile</li>
                    <li>All login sessions</li>
                    <li>OTP verification logs</li>
                    {userToDelete.role === "PROVIDER" && (
                      <>
                        <li>Provider profile</li>
                        <li>Associated venues will be marked as deleted</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={closeDeleteDialog}
              className="rounded-none border-2"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 rounded-none"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={user360Open} onOpenChange={setUser360Open}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-360-dialog">
          <DialogHeader>
            <DialogTitle>User 360 View</DialogTitle>
            <DialogDescription>Complete profile, persona, venue, booking, and activity context for this user.</DialogDescription>
          </DialogHeader>
          {user360 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 border rounded-lg p-3"><p className="text-xs text-muted-foreground">Personas</p><p className="font-heading text-xl font-bold">{(user360.user.personas || []).length}</p></div>
                <div className="bg-slate-50 border rounded-lg p-3"><p className="text-xs text-muted-foreground">Venues</p><p className="font-heading text-xl font-bold">{user360.stats?.venues_count || 0}</p></div>
                <div className="bg-slate-50 border rounded-lg p-3"><p className="text-xs text-muted-foreground">Bookings</p><p className="font-heading text-xl font-bold">{user360.stats?.bookings_count || 0}</p></div>
                <div className="bg-slate-50 border rounded-lg p-3"><p className="text-xs text-muted-foreground">Sessions</p><p className="font-heading text-xl font-bold">{user360.stats?.sessions_count || 0}</p></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card><CardHeader><CardTitle className="text-sm">Profile</CardTitle></CardHeader><CardContent className="space-y-2 text-sm"><p><span className="text-muted-foreground">Name:</span> {user360.user.name}</p><p><span className="text-muted-foreground">Email:</span> {user360.user.email}</p><p><span className="text-muted-foreground">Phone:</span> {user360.user.phone || '—'}</p><p><span className="text-muted-foreground">Status:</span> {user360.user.status || 'ACTIVE'}</p><p><span className="text-muted-foreground">Roles:</span> {(user360.user.personas || []).join(', ') || '—'}</p></CardContent></Card>
                <Card><CardHeader><CardTitle className="text-sm">Provider / SP Context</CardTitle></CardHeader><CardContent className="space-y-2 text-sm"><p><span className="text-muted-foreground">Provider ID:</span> {user360.user.provider_id || '—'}</p><p><span className="text-muted-foreground">Venues:</span> {user360.venues?.length || 0}</p><p><span className="text-muted-foreground">Coach Sessions:</span> {user360.coach_sessions?.length || 0}</p><p><span className="text-muted-foreground">SP Profile:</span> {user360.sp_profile?.verification_status || '—'}</p></CardContent></Card>
              </div>

              <Card><CardHeader><CardTitle className="text-sm">Recent Bookings</CardTitle></CardHeader><CardContent className="space-y-2">{(user360.recent_bookings || []).slice(0, 8).map((booking) => <div key={booking.booking_id} className="text-sm border-b last:border-0 py-2"><div className="flex items-center justify-between"><span>{booking.venue_name || booking.resource_name || booking.booking_id}</span><Badge variant="outline">{booking.status}</Badge></div><p className="text-xs text-muted-foreground">{booking.start_dt?.slice(0, 16).replace('T', ' ') || booking.created_at?.slice(0, 10)}</p></div>)}</CardContent></Card>

              <Card><CardHeader><CardTitle className="text-sm">Recent Login Activity</CardTitle></CardHeader><CardContent className="space-y-2">{(user360.login_logs || []).slice(0, 8).map((log, idx) => <div key={idx} className="text-sm border-b last:border-0 py-2"><div className="flex items-center justify-between"><span>{log.event || log.action}</span><Badge variant="outline">{String(log.success)}</Badge></div><p className="text-xs text-muted-foreground">{log.created_at?.slice(0, 16).replace('T', ' ') || log.timestamp?.slice(0, 16).replace('T', ' ')}</p></div>)}</CardContent></Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
